import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { sendOtp, verifyOtp, registerUser } from "../api/auth.js";
import OtpInputs from "../components/OtpInputs.jsx";
import { useAuth } from "../context/AuthContext.jsx";
import { isValidIndianPhoneInput, sanitizePhoneInputDigits } from "../utils/phone.js";

export default function RegisterPage() {
  const nav = useNavigate();
  const [search] = useSearchParams();
  const next = search.get("next") || "/";

  const { login } = useAuth();

  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [step, setStep] = useState("details"); // details | otp
  const [request, setRequest] = useState(null);
  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(0);
  const [loading, setLoading] = useState(false);

  const validPhone = isValidIndianPhoneInput(form.phone);
  const validEmail = !form.email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  const canDetails = form.name.trim() && validPhone && validEmail;

  useEffect(() => {
    if (timer <= 0) return;
    const t = setTimeout(() => setTimer((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [timer]);

  // const onSend = async () => {
  //   try {
  //     setLoading(true);
  //     const res = await sendOtp(form.phone);
  //     setRequest({ ...(res || {}), phone: form.phone });
  //     setStep("otp");
  //     setTimer(30);
  //   } catch (e) {
  //     alert(e.message || "Failed to send OTP");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // inside RegisterPage.jsx

  // const onVerify = async () => {
  //   try {
  //     setLoading(true);
  //     const resp = await registerUser({
  //       name: form.name,
  //       email: form.email,
  //       phone: form.phone,
  //       code: otp,
  //     });

  //     const token = resp?.token || resp?.jwt || "demo_jwt_" + Date.now();
  //     const profile = resp?.user || resp?.profile || resp;
  //     const finalUser = {
  //       id: profile?.id ?? form.phone,
  //       name: profile?.name ?? form.name ,
  //       phone: profile?.phone ?? form.phone,
  //       role: profile?.role,
  //     };

  //     login(finalUser, token);
  //     nav(next, { replace: true });
  //   } catch (e) {
  //     alert(e.message || "Verification failed");
  //   } finally {
  //     setLoading(false);
  //   }
  // };


  const onSend = async () => {
    try {
      setLoading(true);
      const resp = await registerUser({
        name: form.name,
        email: form.email,
        phone: form.phone,
        // code: otp,
      });
      setRequest({ ...(resp || {}), phone: form.phone });
      setStep("otp");
      setTimer(30);
    } catch (e) {
      alert(e.message || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const onVerify = async () => {
    try {
      setLoading(true);
      const resp = await verifyOtp({ mobile: form.phone, code: otp });
      
      const token = resp?.token || resp?.jwt || "demo_jwt_" + Date.now();
      const profile = resp?.data || resp?.profile || resp;
      const finalUser = {
        id: profile?._id ?? form.phone,
        name: profile?.name ?? form.name,
        phone: profile?.mobile ?? form.phone,
        role: profile?.role,
      };

      login(finalUser, token);
      nav(next, { replace: true });
    } catch (e) {
      alert(e.message || "Verification failed");
    } finally {
      setLoading(false);
    }

    // } catch (e) {
    //   alert(e.message || "OTP verification failed");
    // } finally {
    //    setLoading(false);
    // }

  };


  const onResend = async () => {
    if (timer > 0) return;
    await onSend();
  };

  return (
    <>
      <section className="w-full bg-[#fff8e1] border-b">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-5">
          <h1 className="text-xl font-extrabold text-slate-900">Create Account</h1>
          <p className="text-sm text-slate-700">Register with your mobile number using OTP.</p>
        </div>
      </section>

      <main className="w-full">
        <div className="mx-auto max-w-md px-4 md:px-6 py-6">
          <div className="rounded-2xl border bg-white shadow-sm p-5">
            {step === "details" ? (
              <>
                <div className="grid gap-3">
                  <div>
                    <label className="text-sm text-slate-600">Full Name</label>
                    <input
                      className="mt-1 w-full rounded-lg border px-3 py-2 outline-none focus:ring-2 focus:ring-amber-300"
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-600">Email (optional)</label>
                    <input
                      className="mt-1 w-full rounded-lg border px-3 py-2 outline-none focus:ring-2 focus:ring-amber-300"
                      value={form.email}
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      placeholder="you@example.com"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-600">Mobile Number</label>
                    <div className="mt-1 flex">
                      <span className="inline-flex items-center rounded-l-lg border border-r-0 px-3 text-slate-600 bg-slate-50">
                        +91
                      </span>
                      <input
                        className="flex-1 rounded-r-lg border px-3 py-2 outline-none focus:ring-2 focus:ring-amber-300"
                        placeholder="10-digit mobile"
                        value={form.phone}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            phone: sanitizePhoneInputDigits(e.target.value),
                          }))
                        }
                      />
                    </div>
                  </div>
                </div>

                <button
                  disabled={!canDetails || loading}
                  onClick={onSend}
                  className={`mt-4 w-full rounded-full px-5 py-2.5 font-semibold ${canDetails && !loading
                    ? "bg-amber-400 text-slate-900 hover:bg-amber-300"
                    : "bg-slate-200 text-slate-500 cursor-not-allowed"
                    }`}
                >
                  {loading ? "Sending..." : "Send OTP"}
                </button>

                <p className="mt-4 text-sm text-slate-600">
                  Already have an account?{" "}
                  <Link to="/login" className="text-amber-700 font-semibold hover:underline">
                    Login
                  </Link>
                </p>
              </>
            ) : (
              <>
                <div className="text-sm text-slate-700">
                  OTP sent to <span className="font-semibold">+91 {form.phone}</span>
                </div>

                <div className="mt-3">
                  <OtpInputs value={otp} onChange={setOtp} />
                </div>

                <div className="mt-3 flex items-center justify-between">
                  <button className="text-sm text-slate-600 hover:underline" onClick={() => setStep("details")}>
                    Edit details
                  </button>
                  <button
                    onClick={onResend}
                    disabled={timer > 0 || loading}
                    className={`text-sm font-semibold ${timer > 0 ? "text-slate-400" : "text-amber-700 hover:underline"
                      }`}
                  >
                    {timer > 0 ? `Resend in 0:${String(timer).padStart(2, "0")}` : "Resend OTP"}
                  </button>
                </div>

                <button
                  disabled={otp.length !== 6 || loading}
                  onClick={onVerify}
                  className={`mt-4 w-full rounded-full px-5 py-2.5 font-semibold ${otp.length === 6 && !loading
                    ? "bg-amber-400 text-slate-900 hover:bg-amber-300"
                    : "bg-slate-200 text-slate-500 cursor-not-allowed"
                    }`}
                >
                  {loading ? "Verifying..." : "Verify & Create Account"}
                </button>
              </>
            )}
          </div>
        </div>
      </main>
    </>
  );
}
